import { ArrowRight } from "lucide-react";
import { CtaInstall } from "./cta-install";
import { LatestSignature } from "./latest-signature";
import { PullSignatureButton } from "./pull-signature";
import { usePrices } from "@/lib/prices-context";

export function Hero() {
  const { connState } = usePrices();

  const stateLabel =
    connState === "live"
      ? "LIVE"
      : connState === "stale"
      ? "STALE"
      : connState === "offline"
      ? "OFFLINE"
      : "CONNECTING";

  const stateColor =
    connState === "live"
      ? "text-emerald border-emerald/40"
      : connState === "stale"
      ? "text-amber border-amber/40"
      : connState === "offline"
      ? "text-red border-red/40"
      : "text-amber border-amber/40";

  return (
    <section className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-20 pb-24 sm:px-10 sm:pt-28 sm:pb-32">
      {/* Two-column layout on desktop: editorial copy left, live signature right. */}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] lg:items-center lg:gap-16">
        {/* LEFT: chip + headline + subhead + CTAs */}
        <div>
          {/* Amber chip — Stellar tie-in. The only place amber leads. */}
          <p
            className={`inline-flex items-center gap-2 rounded-full border bg-amber/[0.04] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] ${stateColor}`}
          >
            <span className="relative inline-flex size-1.5">
              <span className="absolute inset-0 rounded-full bg-current animate-pulse" />
            </span>
            STELLAR · TESTNET · <span className="tabular">{stateLabel}</span>
          </p>

          {/* Editorial serif headline. Italic emphasis on the verbs. */}
          <h1 className="mt-8 font-serif text-[40px] leading-[1.04] tracking-[-0.012em] text-text sm:text-6xl xl:text-7xl">
            Prices, <em className="italic text-emerald">fresh</em> by the millisecond.
            <br />
            <span className="text-m70">
              Verified inside <em className="italic">your</em> transaction.
            </span>
          </h1>

          {/* Subhead — sans, calm, factual. */}
          <p className="mt-8 max-w-xl text-base leading-relaxed text-m70 sm:text-[17px]">
            The first pull-based price oracle for Stellar. Fetch a freshly signed
            price from the attestation service and bundle its on-chain verification
            into your own transaction — your contract executes against state
            signed within the last
            <span className="text-text font-mono"> ~500 ms</span>, not against
            pre-warmed ledger state.
          </p>

          {/* CTA cluster. */}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <CtaInstall />
            <a
              href="https://docs.noeracle.org/get-started/quickstart"
              target="_blank"
              rel="noopener"
              className="group inline-flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-m70 hover:text-emerald transition-colors"
            >
              Quickstart
              <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>

          {/* Pull-a-signature, secondary row below the primary CTAs. */}
          <div className="mt-4">
            <PullSignatureButton />
          </div>
        </div>

        {/* RIGHT: the live signature panel — single calm focal element. */}
        <div className="flex items-center justify-center lg:justify-end">
          <LatestSignature />
        </div>
      </div>
    </section>
  );
}
